import React from "react";
import moment from 'moment';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';

const sessionColumns = [
    { id: 1, label: "Session 01", keys: ["session01", "session1", "firstSession"] },
    { id: 2, label: "Session 02", keys: ["session02", "session2", "secondSession"] },
    { id: 3, label: "Session 03", keys: ["session03", "session3", "thirdSession"] }
];

const captureModeColumns = [
    { id: 6, label: "Manual", keys: ["manual", "manualReg", "manualWeight", "manualQty", "manualAmount"] },
    { id: 3, label: "QR", keys: ["qr", "qrCard", "qrWeight", "qrQty", "qrAmount"] },
    { id: 1, label: "Face ID", keys: ["faceID", "faceId", "face", "faceWeight", "faceIDWeight"] }
];

export default class ComponentToPrint extends React.Component {
    normalizeText(value) {
        return value === undefined || value === null ? "" : value.toString().toLowerCase().replace(/[\s_-]/g, "");
    }

    getRawValue(row, fieldNames) {
        const exactValue = fieldNames.map(fieldName => row[fieldName]).find(fieldValue => fieldValue !== undefined && fieldValue !== null && fieldValue !== "");
        if (exactValue !== undefined) {
            return exactValue;
        }

        const rowKeys = Object.keys(row);
        const matchedKey = fieldNames
            .map(fieldName => this.normalizeText(fieldName))
            .map(fieldName => rowKeys.find(rowKey => this.normalizeText(rowKey) === fieldName))
            .find(rowKey => rowKey !== undefined);

        return matchedKey === undefined ? undefined : row[matchedKey];
    }

    getDisplayValue(row, fieldNames) {
        const value = fieldNames.map(fieldName => row[fieldName]).find(fieldValue => fieldValue !== undefined && fieldValue !== null && fieldValue !== "");
        return value === undefined ? "-" : value;
    }

    getRowDate(row) {
        const value = this.getDisplayValue(row, ["date", "collectionDate", "pluckingDate", "workDate", "createdDate"]);
        return value == "-" ? "-" : moment(value).format("YYYY-MM-DD");
    }

    getRowTime(row) {
        const value = this.getDisplayValue(row, ["time", "collectionTime", "pluckingTime", "createdTime", "createdDate"]);
        if (typeof value === "string" && value.includes(":") && !value.includes("-") && !value.includes("T")) {
            return value;
        }

        return value == "-" ? "-" : moment(value).format("HH:mm");
    }

    getRowSessionID(row) {
        const sessionID = this.getRawValue(row, ["sessionID", "sessionId", "mobileSessionID", "mobileSessionId", "sessionNo", "sessionNumber"]);
        const parsedSessionID = parseInt(sessionID);
        if (!isNaN(parsedSessionID)) {
            return parsedSessionID;
        }

        const sessionName = this.normalizeText(this.getRawValue(row, ["sessionName", "session"]));
        const matchedSession = sessionColumns.find(session =>
            sessionName === this.normalizeText(session.label) ||
            session.keys.some(key => sessionName === this.normalizeText(key))
        );

        return matchedSession === undefined ? null : matchedSession.id;
    }

    getRowCaptureModeID(row) {
        const modeID = this.getRawValue(row, ["modeID", "modeId", "captureModeID", "captureModeId", "captureMode"]);
        const parsedModeID = parseInt(modeID);
        if (!isNaN(parsedModeID)) {
            return parsedModeID;
        }

        const modeName = this.normalizeText(this.getRawValue(row, ["mode", "modeName", "captureModeName"]));
        const matchedMode = captureModeColumns.find(mode =>
            modeName === this.normalizeText(mode.label) ||
            mode.keys.some(key => modeName === this.normalizeText(key))
        );

        return matchedMode === undefined ? null : matchedMode.id;
    }

    formatCaptureEntry(row, value) {
        if (value === undefined || value === null || value === "") {
            return null;
        }
        const kg = typeof value === "string" && value.toLowerCase().includes("kg")
            ? value
            : `${value} Kg`;
        const narration = (row.narration || row.Narration || "").toString().trim();
        return { kg, narration };
    }

    getDirectSessionValue(row, session, mode) {
        const fieldNames = [];

        session.keys.forEach(sessionKey => {
            mode.keys.forEach(modeKey => {
                fieldNames.push(`${sessionKey}${modeKey}`);
                fieldNames.push(`${sessionKey}${modeKey.charAt(0).toUpperCase()}${modeKey.slice(1)}`);
            });
        });

        return this.getRawValue(row, fieldNames);
    }

    getCaptureAmount(row) {
        return this.getRawValue(row, ["weightInKg", "weightKg", "amount", "totalAmount", "quantity", "qty", "weight", "totalWeight", "leafWeight", "greenLeafWeight"]);
    }

    getSessionCaptureValue(row, session, mode) {
        const directValue = this.getDirectSessionValue(row, session, mode);
        if (directValue !== undefined) {
            return this.formatCaptureEntry(row, directValue);
        }
        if (this.getRowSessionID(row) === session.id && this.getRowCaptureModeID(row) === mode.id) {
            return this.formatCaptureEntry(row, this.getCaptureAmount(row));
        }
        return null;
    }

    getCapturePercentages(reportData) {
        return sessionColumns.reduce((sessionTotals, session) => {
            const modeCounts = {};
            let sessionTotal = 0;

            captureModeColumns.forEach(mode => {
                modeCounts[mode.id] = reportData.filter(row => this.getSessionCaptureValue(row, session, mode) !== null).length;
                sessionTotal += modeCounts[mode.id];
            });

            sessionTotals[session.id] = captureModeColumns.reduce((percentageTotals, mode) => {
                percentageTotals[mode.id] = sessionTotal === 0 ? "0%" : `${Math.round((modeCounts[mode.id] / sessionTotal) * 100)}%`;
                return percentageTotals;
            }, {});

            return sessionTotals;
        }, {});
    }

    groupReportData(data) {
        const grouped = {};

        data.forEach(row => {
            const key = [
                this.getRowDate(row),
                this.getRowTime(row),
                this.getDisplayValue(row, ["empNo", "employeeNumber", "registrationNumber", "empCode"]),
                this.getDisplayValue(row, ["empName", "employeeName", "firstName", "name"]),
                this.getDisplayValue(row, ["workedDivision", "workedDivisionName", "collectionPointName", "divisionName"]),
                this.getDisplayValue(row, ["field", "fieldName", "blockName"]),
                this.getDisplayValue(row, ["operator", "operatorName", "userName", "createdByName", "createdBy"])
            ].join("|");

            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(row);
        });

        return Object.values(grouped);
    }

    render() {
        const reportData = Array.isArray(this.props.reportData) ? this.props.reportData : [];
        const searchData = this.props.searchData || {};
        const percentages = this.getCapturePercentages(reportData);
        const borderedCellStyle = { fontSize: "10px", padding: "4px" };
        const boldBorderedCellStyle = { ...borderedCellStyle, fontWeight: "bold" };

        return (
            <div className="pl-2 pr-2">
                <h3><center><u>Worker Capture Audit Report</u></center></h3>
                <div className="row pb-3 pt-3">
                    <div className="col"><b>Group: </b>{searchData.group || "-"}</div>
                    <div className="col"><b>Operation Entity: </b>{searchData.estate || "-"}</div>
                    <div className="col"><b>Collection Point: </b>{searchData.division || "All"}</div>
                    <div className="col"><b>Mode: </b>{searchData.mode || "All"}</div>
                </div>
                <div className="row pb-3">
                    <div className="col"><b>Type: </b>{searchData.type || "All"}</div>
                    <div className="col"><b>From Date: </b>{searchData.fromDate || "-"}</div>
                    <div className="col"><b>To Date: </b>{searchData.toDate || "-"}</div>
                </div>
                <Table size="small" aria-label="worker capture audit pdf table" style={{ borderCollapse: "collapse" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Date</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Time</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Emp No</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Emp Name</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>worked<br />division</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Field</TableCell>
                            <TableCell rowSpan={2} align="center" style={boldBorderedCellStyle}>Oparator</TableCell>
                            {sessionColumns.map(session => (
                                <TableCell key={session.id} colSpan={3} align="center" style={boldBorderedCellStyle}>
                                    {session.label}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {sessionColumns.map(session => (
                                captureModeColumns.map(mode => (
                                    <TableCell key={`${session.id}-${mode.id}`} align="center" style={boldBorderedCellStyle}>
                                        {mode.label}
                                    </TableCell>
                                ))
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reportData.length > 0 ? this.groupReportData(reportData).map((rowGroup, groupIndex) => {
                            const firstRow = rowGroup[0];
                            return (
                                <TableRow key={groupIndex}>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getRowDate(firstRow)}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getRowTime(firstRow)}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getDisplayValue(firstRow, ["empNo", "employeeNumber", "registrationNumber", "empCode"])}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getDisplayValue(firstRow, ["empName", "employeeName", "firstName", "name"])}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getDisplayValue(firstRow, ["workedDivision", "workedDivisionName", "collectionPointName", "divisionName"])}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getDisplayValue(firstRow, ["field", "fieldName", "blockName"])}</TableCell>
                                    <TableCell align="left" style={borderedCellStyle}>{this.getDisplayValue(firstRow, ["operator", "operatorName", "userName", "createdByName", "createdBy"])}</TableCell>
                                    {sessionColumns.map(session => (
                                        captureModeColumns.map(mode => {
                                            const entries = rowGroup
                                                .map(row => this.getSessionCaptureValue(row, session, mode))
                                                .filter(v => v !== null);
                                            return (
                                                <TableCell key={`${session.id}-${mode.id}`} align="center" style={borderedCellStyle}>
                                                    {entries.length > 0 ? (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                            {entries.map((entry, i) => (
                                                                <div key={i} style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "space-between",
                                                                    gap: "3px",
                                                                    borderBottom: i < entries.length - 1 ? "1px dashed #aaa" : "none",
                                                                    paddingBottom: i < entries.length - 1 ? "2px" : "0"
                                                                }}>
                                                                    <span style={{ fontSize: "9px" }}>{entry.kg}</span>
                                                                    {entry.narration ? (
                                                                        <span style={{
                                                                            fontSize: "7px",
                                                                            fontWeight: 700,
                                                                            padding: "1px 3px",
                                                                            borderRadius: "6px",
                                                                            whiteSpace: "nowrap",
                                                                            background:
                                                                                entry.narration.toUpperCase() === "GENERAL" ? "#e3f2fd" :
                                                                                    entry.narration.toUpperCase() === "KILO" ? "#f3e5f5" :
                                                                                        entry.narration.toUpperCase() === "RSM" ? "#e8f5e9" :
                                                                                            entry.narration.toUpperCase() === "CASH" ? "#fff8e1" : "#f5f5f5",
                                                                            color:
                                                                                entry.narration.toUpperCase() === "GENERAL" ? "#1565c0" :
                                                                                    entry.narration.toUpperCase() === "KILO" ? "#6a1b9a" :
                                                                                        entry.narration.toUpperCase() === "RSM" ? "#2e7d32" :
                                                                                            entry.narration.toUpperCase() === "CASH" ? "#e65100" : "#555",
                                                                            border: "1px solid currentColor"
                                                                        }}>
                                                                            {entry.narration.toUpperCase()}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : ""}
                                                </TableCell>
                                            );
                                        })
                                    ))}
                                </TableRow>
                            );
                        }) :
                            <TableRow>
                                <TableCell colSpan={16} align="center" style={borderedCellStyle}>No records found</TableCell>
                            </TableRow>
                        }
                        {reportData.length > 0 ?
                            <TableRow>
                                <TableCell colSpan={7} align="center" style={boldBorderedCellStyle}>
                                    Capturing Percentages
                                </TableCell>
                                {sessionColumns.map(session => (
                                    captureModeColumns.map(mode => (
                                        <TableCell key={`${session.id}-${mode.id}-percentage`} align="center" style={boldBorderedCellStyle}>
                                            {percentages[session.id][mode.id]}
                                        </TableCell>
                                    ))
                                ))}
                            </TableRow>
                            : null}
                    </TableBody>
                </Table>
            </div>
        );
    }
}
